/**
 * warehouse-inventory.js
 *
 * Warehouse-aware add-to-cart validation for the Kennicott theme.
 *
 * PDP: hides add-to-cart + buy-now buttons, shows unavailability notice.
 *      Also renders Showroom 1881 badge and lead time for the customer's warehouse.
 * PLP: hides quick-add button, shows unavailability badge on the card.
 *
 * WHY WE USE THE APP PROXY INSTEAD OF LIQUID:
 *   Kennicott uses Shopify's new customer accounts. With new customer accounts,
 *   the Liquid `customer` object is null on storefront pages — the session is
 *   managed by the customer account app, not the theme. This means
 *   `customer.current_company.metafields.company.primary_warehouse` always
 *   returns blank, so the old Liquid-based approach silently fails for all
 *   logged-in B2B customers.
 *
 *   The fix: call the app proxy endpoint /a/account/api/customer/warehouse
 *   which is authenticated by Shopify's HMAC proxy signature and returns
 *   the customer's primary_warehouse metafield value from the Admin API.
 *
 * FAIL-SAFE: if the proxy call fails or returns no warehouse, buttons are
 *   never blocked — we fail open rather than blocking valid customers.
 *   The checkout validation Function is the hard gate; this is UX feedback.
 */

import { ThemeEvents } from '@theme/events';

const UNAVAILABLE_MESSAGE =
  'This product is not available at your warehouse. Please contact your local Kennicott branch to order.';
const CARD_BADGE_MESSAGE = 'Not available at your warehouse';
const NOTICE_ID = 'warehouse-unavailable-notice';
const LEAD_TIME_ID = 'warehouse-lead-time';
const SHOWROOM_BADGE_ID = 'warehouse-showroom-1881-badge';
const DATA_SCRIPT_ID = 'warehouse-inventory-data';
const BLOCKED_ATTR = 'data-warehouse-blocked';

// App proxy URL — matches shopify.app.toml [app_proxy] config
// subpath="account" prefix="a" → /a/account/
const WAREHOUSE_PROXY_URL = '/a/account/api/customer/warehouse';

/** @type {string | null | undefined} undefined = not yet fetched, null = fetched but none */
let _resolvedWarehouse = /** @type {string | null | undefined} */ (undefined);

/**
 * Fetch the customer's primary_warehouse NS location ID via the app proxy.
 * The proxy is authenticated by Shopify's HMAC signature — no token needed client-side.
 * Result is cached for the page session (warehouse doesn't change mid-session).
 *
 * @returns {Promise<string | null>}
 */
async function fetchCustomerWarehouse() {
  if (_resolvedWarehouse !== undefined) return _resolvedWarehouse;

  try {
    const res = await fetch(WAREHOUSE_PROXY_URL, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    });

    if (!res.ok) {
      _resolvedWarehouse = null;
      return null;
    }

    const json = /** @type {WarehouseResponse} */ (await res.json());

    // Only block when definitively false — null means error/unknown, fail-open
    if (json.hasCompany === false) {
      _resolvedWarehouse = 'NO_COMPANY';
      blockCheckoutForNonCompanyCustomer();
      return _resolvedWarehouse;
    }

    _resolvedWarehouse = json.warehouseId ?? null;
    return _resolvedWarehouse;

  } catch {
    _resolvedWarehouse = null;
    return null;
  }
}

/**
 * @typedef {{
 *   customerLoggedIn: boolean,
 *   customerWarehouse: string | null,
 *   customerShopifyLocationGids: string | null,
 *   warehouseQuantities: Record<string, number>,
 *   showroom1881: boolean,
 *   leadTimes: Record<string, number>
 * }} InventoryData
 */

/**
 * @typedef {{ warehouseId: string | null, hasCompany: boolean | null }} WarehouseResponse
 */

/**
 * Read the static inventory data block rendered by warehouse-inventory-data.liquid.
 * Provides warehouseQuantities, showroom1881, leadTimes per product.
 * NOTE: customerWarehouse from this block is always null with new customer accounts —
 * use fetchCustomerWarehouse() instead.
 *
 * @param {string} [idSuffix]
 * @returns {InventoryData | null}
 */
function readInventoryDataScript(idSuffix) {
  const id = idSuffix ? `${DATA_SCRIPT_ID}-${idSuffix}` : DATA_SCRIPT_ID;
  const script = document.getElementById(id);
  if (!script) return null;
  try {
    return /** @type {InventoryData} */ (JSON.parse((script.textContent || '').trim()));
  } catch {
    return null;
  }
}

/**
 * Resolve lead time days for the customer's warehouse.
 * customerShopifyLocationGids is a pipe-separated string of dev|prod GIDs.
 *
 * @param {InventoryData} data
 * @returns {number | null}
 */
function resolveLeadTime(data) {
  if (!data.customerShopifyLocationGids || !data.leadTimes) return null;
  const gids = data.customerShopifyLocationGids.split('|');
  for (const gid of gids) {
    const days = data.leadTimes[gid.trim()];
    if (typeof days === 'number') return days;
  }
  return null;
}

/** @param {Element} scope @returns {HTMLElement} */
function getOrCreateNotice(scope) {
  let el = /** @type {HTMLElement | null} */ (scope.querySelector('#' + NOTICE_ID));
  if (!el) {
    el = document.createElement('p');
    el.id = NOTICE_ID;
    el.setAttribute('role', 'status');
    el.setAttribute('hidden', '');
    el.textContent = UNAVAILABLE_MESSAGE;
    const formButtons = scope.querySelector('.product-form-buttons');
    if (formButtons) formButtons.insertAdjacentElement('beforebegin', el);
    else scope.appendChild(el);
  }
  return el;
}

/** @param {Element} scope @returns {HTMLElement} */
function getOrCreateLeadTime(scope) {
  let el = /** @type {HTMLElement | null} */ (scope.querySelector('#' + LEAD_TIME_ID));
  if (!el) {
    el = document.createElement('p');
    el.id = LEAD_TIME_ID;
    el.className = 'warehouse-lead-time';
    el.setAttribute('hidden', '');
    const formButtons = scope.querySelector('.product-form-buttons');
    if (formButtons) formButtons.insertAdjacentElement('afterend', el);
    else scope.appendChild(el);
  }
  return el;
}

/**
 * @param {Element} scope
 * @param {number | null} days
 */
function syncLeadTimeProperty(scope, days) {
  const form = /** @type {HTMLFormElement | null} */ (
    scope.querySelector('form[action*="/cart/add"]') ||
    scope.querySelector('form[data-type="add-to-cart-form"]') ||
    document.querySelector('form[action*="/cart/add"]') ||
    document.querySelector('form[data-type="add-to-cart-form"]')
  );
  if (!form) return;

  const INPUT_NAME = 'properties[Lead Time]';
  let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="properties[Lead Time]"]'));

  if (days === null) {
    if (input) input.remove();
    return;
  }

  const label = days === 1 ? 'Ships in 1 day' : `Ships in ${days} days`;
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = INPUT_NAME;
    form.appendChild(input);
  }
  input.value = label;
}

/** @param {Element} scope @returns {HTMLElement} */
function getOrCreateShowroomBadge(scope) {
  let el = /** @type {HTMLElement | null} */ (scope.querySelector('#' + SHOWROOM_BADGE_ID));
  if (!el) {
    el = document.createElement('span');
    el.id = SHOWROOM_BADGE_ID;
    el.className = 'warehouse-showroom-1881-badge';
    el.textContent = 'Showroom 1881';
    el.setAttribute('hidden', '');
    const title = scope.querySelector('h1, h2, .product__title');
    if (title) title.insertAdjacentElement('beforebegin', el);
    else {
      const formButtons = scope.querySelector('.product-form-buttons');
      if (formButtons) formButtons.insertAdjacentElement('beforebegin', el);
      else scope.prepend(el);
    }
  }
  return el;
}

/** @param {Element} scope */
function blockButtons(scope) {
  scope.querySelector('.product-form-buttons')?.setAttribute(BLOCKED_ATTR, '');
  scope.querySelector('.accelerated-checkout-block')?.setAttribute(BLOCKED_ATTR, '');
}

/** @param {Element} scope */
function unblockButtons(scope) {
  scope.querySelector('.product-form-buttons')?.removeAttribute(BLOCKED_ATTR);
  scope.querySelector('.accelerated-checkout-block')?.removeAttribute(BLOCKED_ATTR);
}

/**
 * Apply warehouse availability check to a PDP section.
 *
 * @param {string | null} customerWarehouse - NS location ID e.g. "20"
 * @param {InventoryData} data
 * @param {Element} scope
 */
function applyWarehouseCheck(customerWarehouse, data, scope) {
  const notice = getOrCreateNotice(scope);
  const leadTimeEl = getOrCreateLeadTime(scope);
  const showroomBadge = getOrCreateShowroomBadge(scope);

  // Showroom 1881 badge — show regardless of login/warehouse state
  if (data.showroom1881) showroomBadge.removeAttribute('hidden');
  else showroomBadge.setAttribute('hidden', '');

  // No warehouse resolved — fail-open, unblock everything
  if (!customerWarehouse) {
    notice.setAttribute('hidden', '');
    leadTimeEl.setAttribute('hidden', '');
    syncLeadTimeProperty(scope, null);
    unblockButtons(scope);
    return;
  }

  // Customer is logged in but has no company — block add-to-cart entirely
  if (customerWarehouse === 'NO_COMPANY') {
    blockButtons(scope);
    notice.textContent = 'Your account is not currently linked to a company account. Please contact customer service.';
    notice.removeAttribute('hidden');
    leadTimeEl.setAttribute('hidden', '');
    syncLeadTimeProperty(scope, null);
    return;
  }

  const qty = data.warehouseQuantities?.[String(customerWarehouse)];
  const isAvailable = typeof qty === 'number' && qty > 0;

  // Check if the currently selected quantity exceeds warehouse stock
  // Read from the quantity input if present
  const quantityInput = /** @type {HTMLInputElement | null} */ (
    scope.querySelector('input[name="quantity"]') ||
    scope.querySelector('.quantity-selector input') ||
    scope.querySelector('[data-quantity-input]')
  );
  const selectedQty = quantityInput ? parseInt(quantityInput.value || '1', 10) : 1;
  const exceedsStock = isAvailable && typeof qty === 'number' && selectedQty > qty;

  if (!isAvailable) {
    blockButtons(scope);
    notice.textContent = UNAVAILABLE_MESSAGE;
    notice.removeAttribute('hidden');
    leadTimeEl.setAttribute('hidden', '');
    syncLeadTimeProperty(scope, null);
  } else if (exceedsStock) {
    // Cap the quantity input to the warehouse max and show a message
    if (quantityInput && typeof qty === 'number') {
      const input = /** @type {HTMLInputElement} */ (quantityInput);
      if (parseInt(input.value, 10) !== qty) {
        input.value = String(qty);
        // Dispatch input event (not change) so the theme's quantity component
        // updates its display without re-triggering our change listener
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    notice.textContent = `Only ${qty} available at your warehouse. Quantity set to maximum.`;
    notice.removeAttribute('hidden');
    unblockButtons(scope);
    leadTimeEl.setAttribute('hidden', '');
    syncLeadTimeProperty(scope, null);
  } else {
    notice.setAttribute('hidden', '');
    unblockButtons(scope);

    if (data.showroom1881) {
      const days = resolveLeadTime(data);
      if (days !== null) {
        leadTimeEl.textContent = days === 1 ? 'Ships in 1 day' : `Ships in ${days} days`;
        leadTimeEl.removeAttribute('hidden');
      } else {
        leadTimeEl.setAttribute('hidden', '');
      }
      syncLeadTimeProperty(scope, days);
    } else {
      leadTimeEl.setAttribute('hidden', '');
      syncLeadTimeProperty(scope, null);
    }
  }
}

/** @param {Element} section */
async function initSection(section) {
  const data = readInventoryDataScript();
  if (!data) return;

  const customerWarehouse = await fetchCustomerWarehouse();
  applyWarehouseCheck(customerWarehouse, data, section);

  section.addEventListener(ThemeEvents.variantUpdate, () => {
    const fresh = readInventoryDataScript();
    if (fresh) applyWarehouseCheck(customerWarehouse, fresh, section);
  });

  // Re-apply when quantity changes so "Only X available" message updates live
  section.addEventListener('change', (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.matches('input[name="quantity"], .quantity-selector input, [data-quantity-input]')) {
      const fresh = readInventoryDataScript();
      if (fresh) applyWarehouseCheck(customerWarehouse, fresh, section);
    }
  });

  const form = section.querySelector('form[action*="/cart/add"]') ||
    section.querySelector('form[data-type="add-to-cart-form"]');
  if (form) {
    form.addEventListener('submit', () => {
      const freshData = readInventoryDataScript();
      if (freshData && customerWarehouse) {
        const qty = freshData.warehouseQuantities?.[String(customerWarehouse)];
        const isAvailable = typeof qty === 'number' && qty > 0;
        if (isAvailable && freshData.showroom1881) {
          syncLeadTimeProperty(section, resolveLeadTime(freshData));
        } else {
          syncLeadTimeProperty(section, null);
        }
      }
    }, { capture: true });
  }
}

/**
 * PLP: applies warehouse check to a single quick-add card.
 *
 * @param {Element} card
 * @param {HTMLElement} cardDataScript
 * @param {string | null} customerWarehouse
 */
function initQuickAddCard(card, cardDataScript, customerWarehouse) {
  /** @type {InventoryData} */
  let data;
  try {
    data = JSON.parse((cardDataScript.textContent || '').trim());
  } catch {
    return;
  }

  if (!customerWarehouse) return;

  if (customerWarehouse === 'NO_COMPANY') {
    card.setAttribute(BLOCKED_ATTR, '');
    card.setAttribute('data-no-company', '');
    return;
  }

  const qty = data.warehouseQuantities?.[String(customerWarehouse)];
  const isAvailable = typeof qty === 'number' && qty > 0;

  if (!isAvailable) {
    card.setAttribute(BLOCKED_ATTR, '');
    if (!card.nextElementSibling?.classList.contains('warehouse-unavailable-badge')) {
      const badge = document.createElement('div');
      badge.className = 'warehouse-unavailable-badge';
      badge.textContent = CARD_BADGE_MESSAGE;
      card.insertAdjacentElement('afterend', badge);
    }
  }
}

// ── Entry point ───────────────────────────────────────────────────────────────

/**
 * Disable checkout buttons for customers with no company association.
 * The add-to-cart button is blocked via CSS (data-warehouse-blocked).
 * The checkout validation Function is the hard server-side gate.
 */
function blockCheckoutForNonCompanyCustomer() {
  const CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    'button[name="checkout"]',
    '.cart__checkout-button',
    '#checkout',
    'input[name="checkout"]',
    '[data-checkout-button]',
    '.shopify-payment-button__button',
    '[data-testid="Checkout-button"]',
  ].join(', ');

  function disableCheckoutButtons() {
    document.querySelectorAll(CHECKOUT_SELECTORS).forEach((btn) => {
      const el = /** @type {HTMLElement} */ (btn);
      el.setAttribute('disabled', 'disabled');
      el.style.opacity = '0.4';
      el.style.cursor = 'not-allowed';
      el.style.pointerEvents = 'none';
    });
  }

  disableCheckoutButtons();
  new MutationObserver(disableCheckoutButtons)
    .observe(document.body, { childList: true, subtree: true });
}

// PDP
const dataScript = document.getElementById(DATA_SCRIPT_ID);
if (dataScript) {
  const scope = dataScript.closest('.shopify-section') || document.body;
  initSection(/** @type {Element} */ (scope));
}

// PLP — fetch warehouse once, then apply to all cards
const plpCards = document.querySelectorAll('quick-add-component[data-product-id]');
if (plpCards.length > 0) {
  fetchCustomerWarehouse().then((customerWarehouse) => {
    if (customerWarehouse === 'NO_COMPANY') {
      plpCards.forEach((card) => {
        card.setAttribute(BLOCKED_ATTR, '');
        card.setAttribute('data-no-company', '');
      });
      const productGrid = document.querySelector('.product-grid-container');
      if (productGrid) {
        new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof Element)) return;
              const cards = node.matches('quick-add-component[data-product-id]')
                ? [node]
                : Array.from(node.querySelectorAll('quick-add-component[data-product-id]'));
              cards.forEach((card) => {
                card.setAttribute(BLOCKED_ATTR, '');
                card.setAttribute('data-no-company', '');
              });
            });
          }
        }).observe(productGrid, { childList: true, subtree: true });
      }
      return;
    }

    plpCards.forEach((card) => initCardIfNeeded(card, customerWarehouse));

    const productGrid = document.querySelector('.product-grid-container');
    if (productGrid) {
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            const cards = node.matches('quick-add-component[data-product-id]')
              ? [node]
              : Array.from(node.querySelectorAll('quick-add-component[data-product-id]'));
            cards.forEach((card) => initCardIfNeeded(card, customerWarehouse));
          });
        }
      }).observe(productGrid, { childList: true, subtree: true });
    }
  });
}

/** @param {Element} card @param {string | null} customerWarehouse */
function initCardIfNeeded(card, customerWarehouse) {
  const productId = card.getAttribute('data-product-id');
  const cardDataScript = document.getElementById(`${DATA_SCRIPT_ID}-${productId}`);
  if (!cardDataScript) return;
  initQuickAddCard(card, /** @type {HTMLElement} */ (cardDataScript), customerWarehouse);
}
